import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { dangerouslyCreateSafeString } from '@atlassiansox/analytics';
import queryString from 'query-string';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { addFlag } from '../actions/flag-actions';
import { dismissGoogleOneTap } from '../actions/google-one-tap-actions';
import {
  checkUsername,
  checkUsernameCaptchaFailure,
  loginPasswordless,
  loginUsernamePassword,
  resetLoginForm,
  showFormErrors,
} from '../actions/login-actions';
import { fetchConsentConfig } from '../actions/marketing-consent-actions';
import Bull from '../components/BulletSeparator';
import ButtonLink from '../components/ButtonLink';
import GoogleOneTap from '../components/GoogleOneTap';
import { PrivacyPolicyLink, UserNoticeLink } from '../components/links';
import LoginForm from '../components/LoginForm';
import SocialLoginForm from '../components/SocialLoginForm';
import SocialLoginPanel from '../components/SocialLoginPanel';
import Whitebox from '../components/WhiteboxPage';
import { getEmailFromLoginHint, useLoginHint } from '../utilities/hooks/login-hint';
import {
  AppConfig,
  Auth0Config,
  CheckUserNamePasswordlessType,
  JWT,
  LoginFieldError,
  LoginGlobalError,
  LoginGlobalInfo,
  OidcContext,
  OidcDisplay,
  SocialProvider,
  URLParameters,
} from '../types';
import {
  appendReferer,
  EventAttributes,
  useAnalyticsClient,
  useOperationalEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { lookupApplication } from '../utilities/applications';
import { isBooleanFeatureFlagSet } from '../utilities/feature-flags';
import { useRecaptchaChallengeObserver } from '../utilities/hooks';
import { ConsentCheckboxState } from '../utilities/marketing-consent';
import { isMobileOidc, isOidcDisplayMode } from '../utilities/oidc/oidc';
import {
  isAppleAuthEnabled,
  isMicrosoftAuthEnabled,
  isSlackAuthEnabled,
  redirectToAppleLogin,
  redirectToGoogleLogin,
  redirectToMicrosoftLogin,
  redirectToSlackLogin,
  SocialLoginRedirectParams,
} from '../utilities/social-login';
import { getSocialLoginEligibility } from '../utilities/social-login-form';
import { LoginPageMessages } from './LoginPage.messages';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { RecaptchaNotice } from '../components/ReCaptchaContainer';
import { ChallengeResponse } from '../hooks/useRecaptcha';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import { usePageState } from '../utilities/hooks/usePageState';
import { State as CobrandingState } from '../reducers/cobranding-reducer';
import Spinner from '@atlaskit/spinner';
import shouldFetchStateApi from '../utilities/should-fetch-state-api';

interface LoginPageAppConfig {
  googleAuthClientId: string;
  auth0Config: Auth0Config;
  recaptchaEnable: boolean;
  recaptchaEnterpriseCheckboxKeySite: string;
}

export interface LoginPageState {
  appConfig: LoginPageAppConfig;
  hashedCsrfToken?: string; // Not returned by state API
  cobranding: CobrandingState;
  csrfToken?: string; // Not returned by state API
  login: {
    error?: LoginGlobalError;
    fieldErrors?: LoginFieldError;
    recoveryToken?: JWT;
  };
  oidcContext: OidcContext | null;
}

export const loginPageId = 'loginScreen';

const RecaptchaNoticeContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const LoginPage = () =>
  shouldFetchStateApi() ? <LoginPageWithFetchedState /> : <LoginPageWithPreloadedState />;

const LoginPageWithFetchedState = () => {
  const { state: stateFromApi, isLoading, isError, error } = usePageState('/login/state');

  if (isError) {
    throw error;
  }
  return isLoading || !stateFromApi ? (
    <Spinner label="loading" />
  ) : (
    <LoginPageComponent {...stateFromApi} />
  );
};

export const LoginPageWithPreloadedState = () => {
  const preloadedState = {
    appConfig: useAppSelector(state => state.appConfig),
    csrfToken: useAppSelector(state => state.csrfToken),
    cobranding: useAppSelector(state => state.cobranding),
    hashedCsrfToken: useAppSelector(state => state.hashedCsrfToken),
    login: useAppSelector(state => state.login),
    oidcContext: useAppSelector(state => state.microbranding.oidcContext),
  };
  return <LoginPageComponent {...preloadedState} />;
};

const LoginPageComponent = ({
  appConfig: {
    googleAuthClientId,
    auth0Config,
    recaptchaEnable: isCaptchaEnabledByConfig,
    recaptchaEnterpriseCheckboxKeySite,
  },
  hashedCsrfToken,
  cobranding,
  csrfToken,
  login,
  oidcContext,
}: LoginPageState) => {
  hashedCsrfToken = hashedCsrfToken || 'TODO: Set for SAS fetch state api flow';
  csrfToken = csrfToken || 'TODO: Set for SAS fetch state api flow';

  const dispatch = useAppDispatch();

  const applicationData = useMemo(() => lookupApplication(cobranding), [cobranding]);

  const {
    isCaptchaRequired,
    isCheckingUsername,
    isErrorShown,
    isLoggingIn,
    isUsernameChecked,
    passwordless,
    nextAttempt,
    redirectUrl,
    redirectType,
    restrictedEmail,
    usernameExists,
    identities,
    fieldErrors,
  } = useAppSelector(state => state.login);
  const { isAnyMobile, isMobileApp, isEmbedded, screenSize } = useAppSelector(
    state => state.microbranding
  );
  const { dismissed: googleOneTapDismissed } = useAppSelector(state => state.googleOneTap);
  const { config: marketingConsentConfig } = useAppSelector(state => state.marketingConsent);

  const application = applicationData?.application;
  const isLoading = isCheckingUsername || isLoggingIn;
  const isLoginSecondStep = isUsernameChecked;
  const isPasswordlessAllowed =
    passwordless !== CheckUserNamePasswordlessType.Disallowed && !isMobileOidc(oidcContext);

  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();
  const loginHint = useLoginHint();

  const queryParams: URLParameters = queryString.parse(location.search);

  const errorCode = useMemo(
    () => (queryParams.errorCode ? ({ code: queryParams.errorCode } as LoginGlobalError) : null),
    [queryParams.errorCode]
  );

  const dispatchCaptchaFailure = useCallback(() => {
    dispatch(checkUsernameCaptchaFailure(navigator.onLine));
  }, [dispatch]);

  useRecaptchaChallengeObserver(loginPageId);

  const infoCode = useMemo(
    () => (queryParams.infoCode ? ({ code: queryParams.infoCode } as LoginGlobalInfo) : null),
    [queryParams.infoCode]
  );

  const [email, setEmail] = useState(
    () => getEmailFromLoginHint(loginHint) || queryParams.email || ''
  );

  // Check which social login options are being shown
  const isMicrosoftButtonEnabled = isMicrosoftAuthEnabled(location);
  const isAppleButtonEnabled = isAppleAuthEnabled(location);
  const isSlackButtonEnabled = isSlackAuthEnabled(location);

  const appendReasonViewingScreen = useCallback(
    (eventAttributes: EventAttributes) => {
      if (infoCode) {
        switch (infoCode.code) {
          case 'migratingExistingUser':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'migratingUser',
            };
          case 'existingUserSignupAttempt':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'existingUserSignupAttempt',
            };
          case 'invitedUser':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'invitedUser',
            };
          default:
            return eventAttributes;
        }
      }
      return eventAttributes;
    },
    [infoCode]
  );

  usePageViewedEvent(loginPageId, {
    ...appendReferer(
      appendReasonViewingScreen({
        isMicrosoftButtonEnabled,
        isAppleButtonEnabled,
        isSlackButtonEnabled,
      })
    ),
    noAccess: !!queryParams.tenant,
  });

  useOperationalEvent({
    page: loginPageId,
    action: 'loadLoginPageSuccessful',
    subject: 'sli',
  });

  const loginRecoveryOptions = useCallback(() => {
    return login.recoveryToken && email
      ? { expectedEmail: email, recoveryToken: login.recoveryToken }
      : undefined;
  }, [email, login.recoveryToken]);

  const handleContinue = useCallback(
    (username: string, challengeResponse?: ChallengeResponse | null) => {
      const query = {
        ...queryParams,
        email: username,
      };

      delete query.errorCode;

      navigate(`/login?${queryString.stringify(query)}${location.hash}`);

      analyticsClient.formSubmittedEvent(loginPageId, 'checkUsername', {
        recaptchaType: challengeResponse?.type,
      });

      dispatch(
        checkUsername({
          analyticsClient: analyticsClient,
          username,
          params: location.search,
          recoveryToken: loginRecoveryOptions()?.recoveryToken,
          challengeResponse,
        })
      );

      dispatch(fetchConsentConfig(username));
    },
    [
      analyticsClient,
      dispatch,
      navigate,
      location.hash,
      location.search,
      loginRecoveryOptions,
      queryParams,
    ]
  );

  const socialLoginEligibility = useMemo(
    () => getSocialLoginEligibility(identities, location, application),
    [application, identities, location]
  );

  const { highlightedSocialProvider, isEligibleForSocialLogin } = socialLoginEligibility;

  const showSocialLogin = isLoginSecondStep && isEligibleForSocialLogin;

  useEffect(() => {
    if (!isLoading && isLoginSecondStep && usernameExists && identities) {
      analyticsClient.trackingEvent({
        page: loginPageId,
        action: 'triggered',
        subject: 'loginSecondStep',
        attributes: { eligibility: socialLoginEligibility },
      });
    }
  }, [
    analyticsClient,
    identities,
    isLoading,
    isLoginSecondStep,
    socialLoginEligibility,
    usernameExists,
  ]);

  useEffect(() => {
    const shouldRedirect =
      !isMobileApp || isOidcDisplayMode(oidcContext, OidcDisplay.LoginWithRedirect);

    if (redirectUrl) {
      window.location.assign(redirectUrl);
    } else if (!isLoading && isUsernameChecked && !usernameExists && shouldRedirect) {
      dispatch(resetLoginForm());

      analyticsClient.operationalEvent({
        page: loginPageId,
        subject: 'loginAttempt',
        action: 'redirectedToSignup',
      });

      if (location.search.length === 0) {
        navigate('/signup?redirectedFrom=login');
      } else {
        navigate(`/signup${location.search}&redirectedFrom=login`);
      }
    }
  }, [
    analyticsClient,
    application,
    dispatch,
    navigate,
    isAnyMobile,
    isLoading,
    isMobileApp,
    isUsernameChecked,
    oidcContext,
    redirectUrl,
    usernameExists,
    location.search,
  ]);

  const socialButtonClickedEvent = useCallback(
    (socialProvider: SocialProvider) => {
      analyticsClient.buttonClickedEvent(
        showSocialLogin ? 'socialLoginForm' : loginPageId,
        `${socialProvider}LoginButton`,
        appendReasonViewingScreen({})
      );
    },
    [analyticsClient, appendReasonViewingScreen, showSocialLogin]
  );

  const handleGoogleSocialLogin = useCallback(() => {
    socialButtonClickedEvent(SocialProvider.Google);

    redirectToGoogleLogin({
      location: window.location,
      source: loginPageId,
      recoveryOptions: loginRecoveryOptions(),
    });
  }, [loginRecoveryOptions, socialButtonClickedEvent]);

  const handleAuth0SocialLogin = useCallback(
    (socialProvider: SocialProvider, redirectFn: (params: SocialLoginRedirectParams) => void) => {
      socialButtonClickedEvent(socialProvider);

      redirectFn({
        location: window.location,
        auth0Config,
        // TS typechecking is dumb here - should be quite temporary
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        csrfToken: csrfToken!,
        source: loginPageId,
        email: email || undefined, // explicitly pass undefined if empty to avoid including empty URL param
        recoveryOptions: loginRecoveryOptions(),
      });
    },
    [auth0Config, csrfToken, email, loginRecoveryOptions, socialButtonClickedEvent]
  );

  const handleMicrosoftLogin = useCallback(
    () => handleAuth0SocialLogin(SocialProvider.Microsoft, redirectToMicrosoftLogin),
    [handleAuth0SocialLogin]
  );

  const handleAppleLogin = useCallback(
    () => handleAuth0SocialLogin(SocialProvider.Apple, redirectToAppleLogin),
    [handleAuth0SocialLogin]
  );
  const handleSlackLogin = useCallback(
    () => handleAuth0SocialLogin(SocialProvider.Slack, redirectToSlackLogin),
    [handleAuth0SocialLogin]
  );

  const handleLogIn = useCallback(
    (
      username: string,
      password: string,
      captchaValue: string | null | undefined,
      marketingConsent: ConsentCheckboxState
    ) => {
      analyticsClient.formSubmittedEvent(
        loginPageId,
        'loginForm',
        appendReasonViewingScreen({
          marketingConsent: dangerouslyCreateSafeString(marketingConsent.state),
        })
      );

      const onLoginError = (code: string) => {
        analyticsClient.trackingEvent({
          page: loginPageId,
          action: 'submitted',
          subject: 'credentials',
          attributes: {
            successfulAuthentication: false,
            errorCode: code,
          },
        });
      };

      dispatch(
        loginUsernamePassword({
          username,
          password,
          captchaValue,
          location: window.location,
          onLoginError,
          marketingConsent,
        })
      );
    },
    [analyticsClient, appendReasonViewingScreen, dispatch]
  );

  const handlePasswordlessLogIn = useCallback(
    async (email: string, captchaValue: string | null | undefined) => {
      const { success } = await dispatch(
        loginPasswordless({ email, captchaValue, location: window.location })
      );

      if (success) {
        dispatch(
          addFlag(
            'success',
            LoginPageMessages.passwordlessSuccessTitle,
            LoginPageMessages.passwordlessSuccessDescription
          )
        );
      }
    },
    [dispatch]
  );

  const handleEditEmail = useCallback(() => dispatch(resetLoginForm()), [dispatch]);

  const handleValidationError = useCallback(
    (errors: LoginFieldError) => dispatch(showFormErrors(errors)),
    [dispatch]
  );

  const onDismissGoogleOneTap = useCallback(() => dispatch(dismissGoogleOneTap()), [dispatch]);

  const handleFormClick = useCallback(() => {
    if (screenSize === 'mobile') {
      onDismissGoogleOneTap();
    }
  }, [onDismissGoogleOneTap, screenSize]);

  const handleEmailChange = useCallback((email: string) => {
    setEmail(email);
  }, []);

  const signupLinkEnabled = useMemo(() => {
    // Due to Apple Store rules, we don't want sign up link on login page in Mobile OAuth case, unless we provide redirection from login to signup
    const shouldRenderSignupLink =
      !isMobileApp || isOidcDisplayMode(oidcContext, OidcDisplay.LoginWithRedirect);
    return queryParams.shide !== 'true' && shouldRenderSignupLink;
  }, [isMobileApp, oidcContext, queryParams.shide]);

  const getSignupLinkElement = (): React.ReactNode => {
    if (signupLinkEnabled) {
      const elementText = <FormattedMessage {...LoginPageMessages.createAnAccount} />;

      return (
        <Link
          to={'/signup' + location.search}
          id="signup"
          className="primary-action"
          onClick={() => {
            analyticsClient.linkClickedEvent(loginPageId, 'signUpLink');
          }}>
          {elementText}
        </Link>
      );
    }

    return null;
  };

  const buildHeader = (): React.ReactNode => {
    if (showSocialLogin) {
      return (
        <FormattedMessage
          tagName="h5"
          {...LoginPageMessages.socialFormTitle}
          values={{
            socialProviderName: highlightedSocialProvider?.replace(/^./, str => str.toUpperCase()),
          }}
        />
      );
    }

    return <FormattedMessage tagName="h5" {...LoginPageMessages.headTitle} />;
  };

  const buildFooter = (): React.ReactNode => {
    return isEmbedded ? undefined : (
      <>
        <ul className="fine-print">
          <li>
            <PrivacyPolicyLink isMobileLink={isMobileOidc(oidcContext)} />
          </li>
          <Bull />
          <li>
            <UserNoticeLink isMobileLink={isMobileOidc(oidcContext)} />
          </li>
        </ul>
        <RecaptchaNoticeContainer>
          <RecaptchaNotice />
        </RecaptchaNoticeContainer>
      </>
    );
  };

  const buildWhiteboxFooter = (resetPasswordLink: string): React.ReactNode => {
    const signupLink = getSignupLinkElement();

    if (showSocialLogin) {
      return (
        <ButtonLink
          id="use-a-different-login-method"
          spacing="none"
          onClick={() => {
            dispatch(resetLoginForm());
          }}>
          <FormattedMessage {...LoginPageMessages.socialFormUseDifferentLoginMethod} />
        </ButtonLink>
      );
    }

    return (
      <ul>
        <li>
          <Link to={resetPasswordLink} id="resetPassword">
            <FormattedMessage id="trouble.logging.in" defaultMessage="Can't log in?" />
          </Link>
        </li>
        {signupLink && (
          <>
            <Bull />
            <li>{signupLink}</li>
          </>
        )}
      </ul>
    );
  };

  const renderSocialLoginPanel = (): React.ReactNode => {
    const SocialLogin = (
      <SocialLoginPanel
        handleGoogleLogin={handleGoogleSocialLogin}
        handleMicrosoftLogin={handleMicrosoftLogin}
        handleAppleLogin={handleAppleLogin}
        handleSlackLogin={handleSlackLogin}
      />
    );

    return isEmbedded || SocialLogin;
  };

  const resetPasswordLink = '/login/resetpassword' + location.search;

  return (
    <GoogleOneTap
      csrfToken={hashedCsrfToken}
      googleAuthClientId={googleAuthClientId}
      source={loginPageId}
      enabled={
        !googleOneTapDismissed && isBooleanFeatureFlagSet('aid-frontend.google.one.tap.enabled')
      }
      recoveryOptions={loginRecoveryOptions()}>
      <Helmet>
        <title>{formatMessage(LoginPageMessages.headTitle)}</title>
      </Helmet>
      <Whitebox
        pageId={loginPageId}
        header={buildHeader()}
        footer={buildFooter()}
        whiteboxFooter={buildWhiteboxFooter(resetPasswordLink)}>
        {showSocialLogin && highlightedSocialProvider ? (
          <SocialLoginForm
            email={email}
            highlightedSocialProvider={highlightedSocialProvider}
            handleGoogleLogin={handleGoogleSocialLogin}
            handleMicrosoftLogin={handleMicrosoftLogin}
            handleAppleLogin={handleAppleLogin}
            handleSlackLogin={handleSlackLogin}
          />
        ) : (
          <>
            <LoginForm
              application={applicationData && applicationData.application}
              isCaptchaEnabledByConfig={isCaptchaEnabledByConfig}
              isCaptchaRequired={isCaptchaRequired}
              isErrorShown={isErrorShown || !!errorCode}
              redirectType={!isLoginSecondStep ? redirectType : null}
              isRedirecting={redirectType !== null}
              isSecondStep={isLoginSecondStep}
              isPasswordlessAllowed={isPasswordlessAllowed}
              isLoading={isLoading}
              nextAttempt={nextAttempt}
              onContinue={handleContinue}
              onLogIn={handleLogIn}
              onPasswordlessLogIn={handlePasswordlessLogIn}
              error={login.error || errorCode}
              info={infoCode}
              fieldErrors={fieldErrors}
              email={email}
              onValidationError={handleValidationError}
              onCaptchaFailure={dispatchCaptchaFailure}
              onEditEmail={handleEditEmail}
              onFormClick={handleFormClick}
              recaptchaEnterpriseCheckboxKeySite={recaptchaEnterpriseCheckboxKeySite}
              resetPasswordLink={resetPasswordLink}
              restrictedEmail={restrictedEmail || queryParams.restrict === 'true'}
              marketingConsentConfig={marketingConsentConfig}
              pageId={loginPageId}
              onEmailChange={handleEmailChange}
            />
            {renderSocialLoginPanel()}
          </>
        )}
      </Whitebox>
    </GoogleOneTap>
  );
};

export default LoginPage;
